// =============================================================================
// Mapbox cache sweep — delete every Mapbox entry past its 30 days from storage,
// not just from service.
//
// Mapbox Product Terms (21 July 2026) cl 2.8.1 limits CACHING on the device to
// 30 days; Jason's ruling counts them from download. `mapboxCacheAgePlugin`
// (mapTileCache.ts) enforces that on READ: an expired entry is refused and
// deleted the moment something asks for it. An entry nobody asks for again is
// never read, so the plugin never sees it, and ExpirationPlugin's maxAgeSeconds
// counts from LAST USE — a tile last read on day 29 stayed on the device to
// about day 59. Never served, but still cached, which is what the clause limits.
//
// So once a day, in idle time after start-up, the page walks the three Mapbox
// caches and puts every entry through the SAME judgement the service worker
// uses on a read (`mapboxCacheAgePlugin.cachedResponseWillBeUsed`): stale,
// unstamped, unparseable or future-stamped entries are deleted by it, fresh
// ones are left alone. No second copy of the 30-day rule exists.
//
// Why page-side and not in the service worker: the SW is GENERATED
// (vite-plugin-pwa generateSW, prompt mode — scripts/assert-sw-mode.mjs), so
// the only hand-written SW code is public/push-sw.js, served verbatim — it
// cannot import the plugin and would have to restate the rule. The page reaches
// the same caches through the Cache API.
//
// It never blocks the UI and never throws: each cache is walked in batches with
// an idle yield between them (a full tile cache holds 15,000 entries), and a
// cache that is missing, can't be opened or throws mid-walk is skipped.
// =============================================================================

import { MAPBOX_CACHE_NAMES, mapboxCacheAgePlugin } from '@/lib/mapTileCache'

/** How often the sweep may run on one device. */
export const MAPBOX_SWEEP_INTERVAL_MS = 24 * 60 * 60 * 1000

/** Entries judged between yields. */
const MAPBOX_SWEEP_BATCH_SIZE = 200

/** Device-wide (the caches are device-wide, not per tenant or per user). */
export const MAPBOX_SWEEP_STORAGE_KEY = 'kf49.mapboxCacheSweep.lastRunAt'

/** How long after start-up the sweep waits before asking for idle time. */
const START_DELAY_MS = 30_000

export interface MapboxSweepResult {
  /** Entries looked at, across every cache that could be read. */
  examined: number
  /** Entries the age rule refused (each deleted, or its delete retried on the
   *  next read / sweep if the delete itself failed). */
  expired: number
  /** Caches that exist but could not be walked to the end. */
  failedCaches: string[]
}

/** Hand the thread back between batches — idle time when the browser offers
 *  it, otherwise a short timer. */
function idleYield(): Promise<void> {
  return new Promise(resolve => {
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback
    if (typeof ric === 'function') ric(() => resolve(), { timeout: 2_000 })
    else setTimeout(resolve, 50)
  })
}

/**
 * Walk every Mapbox cache and delete each entry the 30-day rule refuses.
 * Never throws.
 */
export async function sweepExpiredMapboxEntries(opts: {
  batchSize?: number
  pause?: () => Promise<void>
} = {}): Promise<MapboxSweepResult> {
  const walk: SweepWalk = {
    batchSize: Math.max(1, opts.batchSize ?? MAPBOX_SWEEP_BATCH_SIZE),
    pause: opts.pause ?? idleYield,
  }
  const result: MapboxSweepResult = { examined: 0, expired: 0, failedCaches: [] }
  if (typeof caches === 'undefined') return result
  for (const cacheName of MAPBOX_CACHE_NAMES) {
    try {
      await sweepCache(cacheName, walk, result)
    } catch {
      // Storage error, quota, a cache deleted under us: skip this cache. The
      // read-time check still refuses anything stale in it; the next sweep
      // tries again.
      result.failedCaches.push(cacheName)
    }
  }
  return result
}

interface SweepWalk {
  batchSize: number
  pause: () => Promise<void>
}

/** One cache, in batches with a yield between them. Adds to `result`; throws
 *  if the cache misbehaves (the caller records it and moves on). */
async function sweepCache(cacheName: string, walk: SweepWalk, result: MapboxSweepResult): Promise<void> {
  // `has` first: `open` would CREATE an empty cache on a device that never
  // opened a map.
  if (!(await caches.has(cacheName))) return
  const cache = await caches.open(cacheName)
  const keys = await cache.keys()
  for (let i = 0; i < keys.length; i += walk.batchSize) {
    if (i > 0) await walk.pause()
    for (const request of keys.slice(i, i + walk.batchSize)) {
      const verdict = await judgeEntry(cache, cacheName, request)
      if (verdict !== 'gone') result.examined++
      if (verdict === 'expired') result.expired++
    }
  }
}

/** The service worker's own read-time judgement: keeps an entry inside its 30
 *  days, deletes anything else. 'gone' = evicted since keys(), nothing to judge. */
async function judgeEntry(cache: Cache, cacheName: string, request: Request): Promise<'kept' | 'expired' | 'gone'> {
  const cachedResponse = await cache.match(request)
  if (!cachedResponse) return 'gone'
  const kept = await mapboxCacheAgePlugin.cachedResponseWillBeUsed({ cacheName, request, cachedResponse })
  return kept ? 'kept' : 'expired'
}

// ── Throttle ────────────────────────────────────────────────────────────────

/** Due when it never ran, the record is unreadable, a day has passed, or the
 *  record is in the future (the device clock moved back). */
export function sweepIsDue(lastRunAt: number | null, now: number): boolean {
  if (lastRunAt === null || !Number.isFinite(lastRunAt)) return true
  if (lastRunAt > now) return true
  return now - lastRunAt >= MAPBOX_SWEEP_INTERVAL_MS
}

function readLastRun(): number | null {
  try {
    const raw = globalThis.localStorage?.getItem(MAPBOX_SWEEP_STORAGE_KEY)
    return raw ? Number(raw) : null
  } catch {
    return null // storage blocked: run — a sweep with nothing stale is cheap
  }
}

function writeLastRun(at: number): void {
  try {
    globalThis.localStorage?.setItem(MAPBOX_SWEEP_STORAGE_KEY, String(at))
  } catch {
    // storage blocked or full — the next start-up simply sweeps again
  }
}

let running = false

/**
 * Sweep if the last sweep on this device is a day old (or unknown). Returns the
 * result, or null when it was not due / already running / failed. Never throws.
 * The record is written AFTER the walk, so a sweep cut short by a closed tab is
 * finished on the next start-up rather than a day later.
 */
export async function runMapboxCacheSweepIfDue(
  now: () => number = Date.now,
  sweep: () => Promise<MapboxSweepResult> = sweepExpiredMapboxEntries,
): Promise<MapboxSweepResult | null> {
  if (running) return null
  if (!sweepIsDue(readLastRun(), now())) return null
  running = true
  try {
    const result = await sweep()
    writeLastRun(now())
    return result
  } catch {
    return null
  } finally {
    running = false
  }
}

/** Start-up hook (main.tsx): wait out the boot, then sweep in idle time. */
export function scheduleMapboxCacheSweep(): void {
  if (typeof window === 'undefined' || typeof caches === 'undefined') return
  window.setTimeout(() => {
    void idleYield().then(() => runMapboxCacheSweepIfDue())
  }, START_DELAY_MS)
}
